// Lead Management. Leads come from two places that share ONE store: staff-created leads (this
// module's POST) and visitor chat lead capture (see chat.js -> createLeadRecord).
import { leadsStore, webhooksStore } from "../core";
import { LEAD_STATUSES } from "../fixtures";
import { ok, fail, validationFail, nowIso } from "../helpers";
import { firstMessage, leadFields } from "../validate";

const SEED_DELIVERIES = {
  "tenant-acme": [
    { id: "wh-1", event: "lead.created", url: "https://crm.acme.com/hooks", status: "Delivered", attempts: 1, http: 200, at: "2026-09-11T10:24:11" },
    { id: "wh-2", event: "lead.status_changed", url: "https://crm.acme.com/hooks", status: "Failed", attempts: 3, http: 500, at: "2026-09-11T10:18:40" },
    { id: "wh-3", event: "lead.qualified", url: "https://zap.io/h/8f2", status: "Pending", attempts: 1, http: null, at: "2026-09-11T10:05:02" }
  ]
};

const leadId = () => `L-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 4).toUpperCase()}`;
const clampScore = (n) => Math.max(0, Math.min(100, Math.round(Number(n))));

export function recordWebhook(tenantId, event) {
  const all = webhooksStore.all();
  const list = all[tenantId] || SEED_DELIVERIES[tenantId] || [];
  all[tenantId] = [{ id: `wh-${Date.now().toString(36)}`, event, url: "https://crm.example.com/hooks", status: "Delivered", attempts: 1, http: 200, at: nowIso() }, ...list];
  webhooksStore.save(all);
}

/** Visitor-chat-driven lead score: engagement + stated intent. */
export function scoreLead({ intent, visitorMessages = 0 }) {
  const intentBonus = { "Book a demo": 20, "Pricing enquiry": 15, Support: 5 }[intent] || 0;
  return clampScore(40 + Math.min(32, visitorMessages * 8) + intentBonus);
}

/**
 * Creates a lead (or returns the existing one for the same email in this tenant).
 * Used by POST /api/leads and by the visitor widget's lead capture.
 */
export function createLeadRecord(tenantId, payload) {
  const email = String(payload.email || "").trim().toLowerCase();
  const leads = leadsStore.all();
  const existing = leads.find((l) => l.tenantId === tenantId && l.email.toLowerCase() === email);
  if (existing) return { lead: existing, duplicate: true };
  const createdAt = nowIso();
  const source = payload.source || "Website";
  const intent = payload.intent || "General enquiry";
  const score = clampScore(payload.score ?? 50);
  const lead = {
    id: leadId(), tenantId, name: String(payload.name).trim(), email, phone: String(payload.phone || "").trim(),
    source, intent, status: LEAD_STATUSES.includes(payload.status) ? payload.status : "New", score, createdAt,
    company: String(payload.company || "").trim(), message: String(payload.message || "").trim(),
    timeline: [
      { at: createdAt, event: payload.timelineFirst || `Lead created via ${source}` },
      { at: createdAt, event: `Intent detected: ${intent}` },
      { at: createdAt, event: `Score calculated: ${score}` }
    ]
  };
  leadsStore.save([lead, ...leads]);
  recordWebhook(tenantId, "lead.created");
  return { lead, duplicate: false };
}

export const leadRoutes = [
  {
    method: "GET", pattern: /^\/api\/leads$/, permission: "leads:manage",
    handler: ({ tenantId, query, scenario }) => {
      let items = scenario === "empty" ? [] : leadsStore.all().filter((l) => l.tenantId === tenantId);
      const status = query.get("status");
      if (status) items = items.filter((l) => l.status === status);
      return ok(200, items, { total: items.length, tenantId });
    }
  },
  {
    method: "GET", pattern: /^\/api\/leads\/webhooks\/deliveries$/, permission: "leads:manage",
    handler: ({ tenantId, scenario }) => {
      const list = scenario === "empty" ? [] : (webhooksStore.all()[tenantId] || SEED_DELIVERIES[tenantId] || []);
      return ok(200, list, { total: list.length });
    }
  },
  {
    method: "GET", pattern: /^\/api\/leads\/([\w-]+)$/, permission: "leads:manage",
    handler: ({ tenantId, match }) => {
      const lead = leadsStore.all().find((l) => l.id === match[1] && l.tenantId === tenantId);
      if (!lead) return fail(404, "NOT_FOUND", "Lead not found.");
      return ok(200, lead);
    }
  },
  {
    method: "POST", pattern: /^\/api\/leads$/, permission: "leads:manage",
    handler: ({ tenantId, body }) => {
      const invalid = leadFields(body);
      if (invalid) return validationFail(invalid, firstMessage(invalid));
      const name = String(body.name).trim();
      const email = String(body.email).trim().toLowerCase();
      const { lead, duplicate } = createLeadRecord(tenantId, { ...body, name, email });
      if (duplicate) return fail(409, "LEAD_EXISTS", "A lead with this email already exists.", { fields: { email: "A lead with this email already exists." } });
      return ok(201, lead);
    }
  },
  {
    method: "PATCH", pattern: /^\/api\/leads\/([\w-]+)$/, permission: "leads:manage",
    handler: ({ tenantId, match, body }) => {
      const leads = leadsStore.all();
      const lead = leads.find((l) => l.id === match[1] && l.tenantId === tenantId);
      if (!lead) return fail(404, "NOT_FOUND", "Lead not found.");
      const invalid = leadFields(body, true);
      if (invalid) return validationFail(invalid, firstMessage(invalid));
      const next = { ...lead };
      if (body.name !== undefined) next.name = String(body.name).trim();
      if (body.email !== undefined) next.email = String(body.email).trim().toLowerCase();
      if (leads.some((l) => l.id !== lead.id && l.tenantId === tenantId && l.email.toLowerCase() === next.email)) {
        return fail(409, "LEAD_EXISTS", "Another lead already uses this email.", { fields: { email: "Another lead already uses this email." } });
      }
      ["phone", "source", "intent"].forEach((key) => { if (body[key] !== undefined) next[key] = String(body[key]).trim(); });
      let statusChanged = false;
      if (body.status !== undefined) {
        statusChanged = body.status !== lead.status;
        next.status = body.status;
      }
      if (body.score !== undefined) next.score = clampScore(body.score);
      next.timeline = [...(lead.timeline || [])];
      if (statusChanged) next.timeline.push({ at: nowIso(), event: `Status changed to ${next.status}` });
      else next.timeline.push({ at: nowIso(), event: "Lead details updated" });
      leadsStore.save(leads.map((l) => (l.id === lead.id ? next : l)));
      if (statusChanged) recordWebhook(tenantId, next.status === "Qualified" ? "lead.qualified" : "lead.status_changed");
      return ok(200, next);
    }
  },
  {
    method: "DELETE", pattern: /^\/api\/leads\/([\w-]+)$/, permission: "leads:manage",
    handler: ({ tenantId, match }) => {
      const leads = leadsStore.all();
      const lead = leads.find((l) => l.id === match[1] && l.tenantId === tenantId);
      if (!lead) return fail(404, "NOT_FOUND", "Lead not found.");
      leadsStore.save(leads.filter((l) => l.id !== lead.id));
      return ok(200, lead);
    }
  }
];
